import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import style from './ZingNotifications.module.css'
import DialogModal from '../DialogModal/DialogModal'
import { closeReadComPort } from '../../WebSerialConnection/SerialUSBConnect'
import { isZingNotificationDevice } from '../../../utils/zingNotifications'

function ZingNotificationHost() {
    const dispatch = useDispatch()
    const history = useHistory()
    const { isFalling, motorFaultIds, sdCardMissing } = useSelector(
        (state) => state.zingNotification
    )
    const webSerial = useSelector((state) => state.webSerial)
    const worker = useSelector((state) => state.worker)
    const device = useSelector((state) => state.device)

    useEffect(() => {
        if (!webSerial.isConnected) dispatch({ type: 'ZING_NOTIFICATION_RESET' })
    }, [webSerial.isConnected, dispatch])

    if (
        !webSerial.isConnected ||
        !isZingNotificationDevice(device.connectedDevice, device.deviceVersion)
    )
        return null

    const disconnect = async () => {
        await closeReadComPort(webSerial.port, worker)
        dispatch({ type: 'WEB_SERIAL_PORT_DISCONNECT' })
        dispatch({ type: 'ZING_NOTIFICATION_RESET' })
        history.push('/kitSelection')
    }

    // SD card takes priority; motor fault next. Fall recovery never hides either.
    if (sdCardMissing) {
        return (
            <DialogModal
                show
                preventBackdropClose
                showCloseBtn={false}
                text={
                    <div className={style.dialogBody}>
                        <h3 className={style.dialogTitle}>No SD Card Detected</h3>
                        <p>
                            The SD card is not detected in your Zing device. Please insert
                            the SD card properly and try again.
                        </p>
                    </div>
                }
                optionsToSelect={[{ text: 'OK', do: disconnect }]}
            />
        )
    }

    if (motorFaultIds.length) {
        // No button: the firmware sends the motor-link bytes again once the
        // servo is reconnected, and that clears this automatically.
        return (
            <div
                className={style.fallOverlay}
                role="alertdialog"
                aria-live="assertive"
                aria-label="Servo motor check failed"
                data-testid="zing-motor-overlay"
                onKeyDown={(e) => e.preventDefault()}
                tabIndex={-1}
                ref={(el) => el && el.focus()}
            >
                <div className={style.fallCard}>
                    <span className={style.fallSpinner} />
                    <h3 className={style.fallTitle}>Servo Motor Check Failed</h3>
                    <p className={style.fallText}>
                        {motorFaultIds.length === 1
                            ? 'A servo motor is not responding.'
                            : 'Some servo motors are not responding.'}
                    </p>
                    <p className={style.servoIds}>
                        {motorFaultIds.map((id) => (
                            <span key={id} className={style.servoChip}>
                                Servo {id}
                            </span>
                        ))}
                    </p>
                    <p className={style.fallText}>
                        Please check the motor connection and wiring. This message
                        goes away on its own once the servo is connected again.
                    </p>
                </div>
            </div>
        )
    }

    if (isFalling) {
        // Full-screen blocker: swallows every click/keypress until the firmware
        // reports recovery (the fall notification clears itself).
        return (
            <div
                className={style.fallOverlay}
                role="alertdialog"
                aria-live="assertive"
                aria-label="Zing is recovering from a fall"
                data-testid="zing-fall-overlay"
                onKeyDown={(e) => e.preventDefault()}
                tabIndex={-1}
                ref={(el) => el && el.focus()}
            >
                <div className={style.fallCard}>
                    <span className={style.fallSpinner} />
                    <h3 className={style.fallTitle}>Please wait</h3>
                    <p className={style.fallText}>
                        Zing is recovering from a fall.
                        <br />
                        You can continue once it is back on its feet.
                    </p>
                </div>
            </div>
        )
    }

    return null
}

export default ZingNotificationHost
